import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { queryClient } from 'src/queryClient';
import { store } from 'src/redux/store';
import { resetStoreState } from 'src/redux/reducers';
import { hideSpinnerToast } from 'src/utils/spinnerToaster';
import { clearImageCache } from 'src/components/ui/Image/Image';
import { clearConversations } from 'src/components/layouts/AiChat/aiChatHistory.storage';
import { clearRecentlyVisited } from 'src/components/layouts/CommandPalette/useRecentlyVisited';
import { clearTablePreferences } from 'src/ViewPreferences.storage';
import { clearToken, getToken, setToken, subscribe } from './authToken';

/**
 * Drops every piece of client state that belongs to the signed-in user.
 *
 * Deliberately does *not* touch the token — callers decide whether a session
 * ends (`logout`) or is replaced by a new one (`beginSession`).
 *
 * Deliberately not a `localStorage.clear()` either: `theme`, `language` and
 * `sidebarExpanded` are device preferences and survive. Everything that is
 * *user content* is removed explicitly below — on a shared workstation the next
 * user must not find the previous one's chats, recently opened orders, cached
 * product images or table layouts:
 *
 *  - `ai_chat_conversations` — up to 30 full AI conversations incl. message text
 *  - `cmdk_recent`           — labels of the last opened orders/contacts/products
 *  - `df_view_preferences_v1`— per-model column/filter/sort preferences
 *  - image cache             — base64 product images / company logo
 */
export const resetClientState = () => {
  // Cancel first, then drop the cache, so in-flight requests are aborted
  // instead of writing the old user's data back into a fresh cache.
  queryClient.cancelQueries();
  queryClient.clear();
  store.dispatch(resetStoreState());
  clearConversations();
  clearRecentlyVisited();
  clearTablePreferences();
  clearImageCache();
  // A spinner toast is created with autoClose:false/closeButton:false. If the
  // component that would hide it unmounts first, it sticks to the login screen
  // forever and blocks every later spinner (module-level `toastId`).
  hideSpinnerToast();
  toast.dismiss();
};

/**
 * Last token this module knows about.
 *
 * `logout`/`beginSession` keep it in sync themselves, so the watcher below can
 * tell "the token changed because *this* tab started/ended a session" (already
 * reset, nothing to do) from "the token changed underneath us" (another tab, or
 * a 401 handler dropping an expired token) — which does need a reset.
 */
let lastObservedToken: string | null = getToken();

/** Ends the session: clears client state and removes the token. */
export const logout = () => {
  resetClientState();
  lastObservedToken = null;
  clearToken();
};

/**
 * Starts a session for `token`. Resetting first guarantees the invariant
 * "a new token never sees the previous user's cache" — which also covers the
 * paths that do not go through `logout()` (expired session, cross-tab logout,
 * token login link).
 *
 * Throws `TokenPersistenceError` when the token cannot be persisted (storage
 * disabled/full). Callers must surface that: reporting success while the token
 * is nowhere to be found sends the app into a silent `/login` → `/dashboard` →
 * `/login` loop.
 */
export const beginSession = (token: string) => {
  resetClientState();
  const previous = lastObservedToken;
  lastObservedToken = token;
  try {
    setToken(token);
  } catch (error) {
    lastObservedToken = previous;
    throw error;
  }
};

/**
 * Reacts to a token change this tab did not make.
 *
 * Without it, tab B keeps the React Query cache of user A after tab A logged
 * out and back in as user B — including `['get','/users/user/access_rights/']`,
 * which `Auth.handleAccessControl` and the navigation gate on. Server-side
 * authorization is unaffected, but A's rows would be rendered to B until every
 * query refetched.
 */
export const syncSessionWithToken = () => {
  const token = getToken();
  if (token === lastObservedToken) return;
  lastObservedToken = token;
  resetClientState();
};

/**
 * Starts watching the token store; returns the unsubscribe function.
 * Wired up once in `App`, deliberately not inside `authToken.ts` — that module
 * stays free of app dependencies so every service can import it cycle-free.
 */
export const startSessionWatcher = (): (() => void) => subscribe(syncSessionWithToken);

/**
 * Logout for use inside the React tree: tears the session down and navigates to
 * the login page with `replace`, so the signed-in page does not stay reachable
 * through the back button.
 */
export const useLogout = () => {
  const navigate = useNavigate();

  return useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [navigate]);
};
